import { create } from "zustand";
import { persist } from "zustand/middleware";

import cartService from "@/services/cart/cartService";
import productService from "@/services/products/productService";
import { useAuthStore } from "./authStore";
import { logger } from "@/lib/logger";

export interface CartItem {
  id: string; // productId
  cartItemId?: number; // ID del item en el carrito del backend
  name: string;
  price: number;
  quantity: number;
  image?: string;
  category?: string;
  // POS al que pertenece el producto (solo frontend, para restricción de mezcla)
  posId?: string | number;
}

interface CartStore {
  items: CartItem[];
  // ✅ CAMPOS PARA SESIONES ANÓNIMAS
  anonymousSessionToken: string | null;
  isAnonymous: boolean;

  // Nuevo campo para mostrar loading en operaciones del carrito
  loading: boolean;

  // 🛡️ Flag para evitar cargar carrito después de pago exitoso
  isPostPayment: boolean;

  // 🛡️ Timestamp de última limpieza para evitar recargas inmediatas
  lastClearTimestamp: number | null;

  // ✅ MÉTODOS PARA USUARIOS ANÓNIMOS
  generateAnonymousSession: () => Promise<void>;
  addAnonymousItem: (item: Omit<CartItem, "quantity">) => void;
  removeAnonymousItem: (itemId: string) => void;
  updateAnonymousQuantity: (itemId: string, quantity: number) => void;
  clearAnonymousCart: () => void;
  loadAnonymousCart: () => Promise<void>;
  removeAnonymousItemFromBackend: (cartItemId: number) => Promise<void>;
  updateAnonymousQuantityInBackend: (
    cartItemId: number,
    quantity: number
  ) => Promise<void>;
  updateAnonymousClientData: (clientData: {
    nombre: string;
    email: string;
    telefono?: string;
  }) => Promise<void>;
  convertAnonymousToAuthenticated: () => Promise<void>;
  setAnonymousSessionToken: (sessionToken: string) => void;
  setIsAnonymous: (isAnonymous: boolean) => void;

  // ✅ MÉTODOS PARA USUARIOS AUTENTICADOS (EXISTENTES)
  addItem: (item: Omit<CartItem, "quantity">) => void;
  removeItem: (itemId: string) => void;
  updateQuantity: (itemId: string, quantity: number) => void;
  clearCart: () => void;
  clearCartAfterPayment: () => void; // Método específico para limpiar después del pago
  getTotalItems: () => number;
  getTotalPrice: () => number;
  loadCart: () => Promise<void>;
  removeItemFromBackend: (cartItemId: number) => Promise<void>;
  updateQuantityInBackend: (
    cartItemId: number,
    quantity: number
  ) => Promise<void>;

  // 🚀 MÉTODOS PARA OPTIMISTIC UPDATES
  updateItemQuantityOptimistic: (cartItemId: number, quantity: number) => void;
  removeItemOptimistic: (cartItemId: number) => void;
  clearCartOptimistic: () => void;
  updateItemQuantityOptimisticWithFallback: (
    identifier: number | string,
    quantity: number
  ) => void;
  removeItemOptimisticWithFallback: (identifier: number | string) => void;

  // 🚀 MÉTODOS PARA INTEGRACIÓN CON COLA
  updateUIOptimistic: (action: any) => void;
  getQueueStatus: () => any;
}

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      items: [],
      anonymousSessionToken: null,
      isAnonymous: false,
      loading: false,
      isPostPayment: false,
      lastClearTimestamp: null,

      // ✅ MÉTODOS PARA USUARIOS ANÓNIMOS

      // METODOS PARA CLIENTES ANONIMOS
      generateAnonymousSession: async () => {
        try {
          logger.log(
            "🚀 Generando sesión anónima... usando generateAnonymousSession"
          );
          set({ loading: true });
          const sessionData = await cartService.generateAnonymousSession();
          set({ loading: false });
          set({
            anonymousSessionToken: sessionData.sessionToken,
            isAnonymous: true,
          });
          logger.log("Sesión anónima generada:", sessionData.sessionToken);
        } catch (error) {
          logger.error("Error generando sesión anónima:", error);
          throw error;
        }
      },

      setAnonymousSessionToken: (sessionToken: string) => {
        set({ anonymousSessionToken: sessionToken });
      },

      setIsAnonymous: (isAnonymous: boolean) => {
        set({ isAnonymous: isAnonymous });
      },

      addAnonymousItem: async (newItem) => {
        // 🚀 OPTIMISTIC UPDATE: Actualizar UI inmediatamente
        const optimisticUpdate = () => {
          set((state) => {
            const existingItem = state.items.find((i) => i.id === newItem.id);
            return existingItem
              ? {
                  items: state.items.map((i) =>
                    i.id === newItem.id ? { ...i, quantity: i.quantity + 1 } : i
                  ),
                }
              : { items: [...state.items, { ...newItem, quantity: 1 }] };
          });
        };

        // 1. Actualizar UI inmediatamente (optimistic update)
        optimisticUpdate();

        // 2. Request en background
        try {
          const { anonymousSessionToken } = get();

          if (!anonymousSessionToken) {
            await get().generateAnonymousSession();
          }

          const sessionToken = get().anonymousSessionToken;

          if (!sessionToken) {
            throw new Error("No se pudo generar sesión anónima");
          }

          // 3. Petición al backend en background (sin loading flag)
          await cartService.addToAnonymousCart(
            { productId: newItem.id, quantity: 1 },
            sessionToken
          );

          // 4. Actualizar el item local con el cartItemId del backend

          // LO COMENTE PORQUE NO ENCUENTRA VARIABLES

          // set((state) => ({
          //   items: state.items.map((item) =>
          //     item.id === newItem.id && !item.cartItemId
          //       ? { ...item, cartItemId: addedItem.id }
          //       : item
          //   ),
          // }));

          logger.log("✅ Item agregado exitosamente al backend ");
        } catch (error) {
          // 4. 🔄 ROLLBACK: Revertir cambios si falla
          logger.error("❌ Error agregando al carrito anónimo:", error);

          // Revertir el optimistic update
          set((state) => {
            const existingItem = state.items.find((i) => i.id === newItem.id);
            if (existingItem && existingItem.quantity > 1) {
              // Si ya existía, decrementar cantidad
              return {
                items: state.items.map((i) =>
                  i.id === newItem.id ? { ...i, quantity: i.quantity - 1 } : i
                ),
              };
            } else {
              // Si era nuevo, remover completamente
              return {
                items: state.items.filter((i) => i.id !== newItem.id),
              };
            }
          });

          // Re-lanzar el error para que el componente pueda manejarlo
          throw error;
        }
      },

      removeAnonymousItem: (itemId) => {
        set((state) => ({
          items: state.items.filter((item) => item.id !== itemId),
        }));
      },

      updateAnonymousQuantity: (itemId, quantity) => {
        if (quantity <= 0) {
          get().removeAnonymousItem(itemId);
          return;
        }

        set((state) => ({
          items: state.items.map((item) =>
            item.id === itemId ? { ...item, quantity } : item
          ),
        }));
      },

      clearAnonymousCart: async () => {
        try {
          const { anonymousSessionToken } = get();
          logger.log("🧹 Limpiando carrito anónimo...", {
            hasSessionToken: !!anonymousSessionToken,
          });

          if (anonymousSessionToken) {
            logger.log(
              "🔄 Enviando petición al backend con token:",
              anonymousSessionToken
            );
            const response = await cartService.clearAnonymousCart(
              anonymousSessionToken
            );
            logger.log("✅ Respuesta del backend:", response);
          }

          set({
            items: [],
            // ⚠️ NO eliminar anonymousSessionToken - mantener la sesión
            lastClearTimestamp: Date.now(), // 🛡️ Marcar cuándo se limpió
          });
          logger.log(
            "✅ Carrito anónimo limpiado - items eliminados del estado local"
          );
        } catch (error) {
          logger.error("❌ Error limpiando carrito anónimo:", error);
          set({
            items: [],
            lastClearTimestamp: Date.now(),
          });
          throw error;
        }
      },

      loadAnonymousCart: async () => {
        try {
          logger.log(
            "🚀 Cargando carrito anónimo... usando loadAnonymousCart"
          );
          const { anonymousSessionToken } = get();
          logger.log(
            "anonymousSessionToken from loadAnonymousCart",
            anonymousSessionToken
          );
          if (!anonymousSessionToken) return;

          // 1. Petición al backend para obtener los items del carrito anónimo
          set({ loading: true });
          const backendItems = await cartService.getAnonymousCartItems(
            anonymousSessionToken
          );
          logger.log("backendItems from loadAnonymousCart", backendItems);
          set({ loading: false });

          // TODO: OPTIMIZAR MIGRAR A UNA SOLA PETICION
          const products = await productService.getAllProducts();

          // 2. Transformar los datos al formato del estado local
          const transformedItems = backendItems.map((item: any) => ({
            id: item.productId,
            cartItemId: item.id,
            name:
              products.products.find((p: any) => p.id === item.productId)
                ?.nombre || "",
            price: parseFloat(item.unitPrice),
            quantity: item.quantity,
            image:
              products.products.find((p: any) => p.id === item.productId)
                ?.imagen || "",
          }));

          logger.log("transformedAnonymousItems", transformedItems);

          // 3. Actualizar estado local con los items recuperados
          set({ items: transformedItems as CartItem[] });
        } catch (error) {
          logger.error(
            "No se pudo obtener el carrito anónimo del backend",
            error
          );
        }
      },

      removeAnonymousItemFromBackend: async (cartItemId: number) => {
        try {
          const { anonymousSessionToken } = get();
          if (!anonymousSessionToken) return;

          // 1. Eliminar del backend
          set({ loading: true });
          await cartService.removeAnonymousCartItem(
            cartItemId.toString(),
            anonymousSessionToken
          );
          set({ loading: false });

          // 2. Recargar el carrito desde el backend
          await get().loadAnonymousCart();
        } catch (error) {
          logger.error("Error eliminando item del carrito anónimo:", error);
          throw error;
        }
      },

      updateAnonymousQuantityInBackend: async (
        cartItemId: number,
        quantity: number
      ) => {
        try {
          const { anonymousSessionToken } = get();
          if (!anonymousSessionToken) return;

          // 1. Actualizar en el backend
          set({ loading: true });
          await cartService.updateAnonymousCartItemQuantity(
            cartItemId.toString(),
            { quantity },
            anonymousSessionToken
          );
          set({ loading: false });

          // 2. Recargar el carrito desde el backend
          await get().loadAnonymousCart();
        } catch (error) {
          logger.error("Error actualizando cantidad del item anónimo:", error);
          throw error;
        }
      },

      updateAnonymousClientData: async (clientData) => {
        try {
          const { anonymousSessionToken } = get();
          if (!anonymousSessionToken) {
            throw new Error("No hay sesión anónima activa");
          }

          set({ loading: true });
          await cartService.updateAnonymousClientData(
            clientData,
            anonymousSessionToken
          );
          set({ loading: false });
          logger.log("Datos del cliente anónimo actualizados");
        } catch (error) {
          logger.error("Error actualizando datos del cliente anónimo:", error);
          throw error;
        }
      },

      convertAnonymousToAuthenticated: async () => {
        try {
          const { anonymousSessionToken } = get();
          if (!anonymousSessionToken) {
            throw new Error("No hay sesión anónima activa");
          }

          set({ loading: true });
          await cartService.convertAnonymousToAuthenticated(
            anonymousSessionToken
          );
          set({ loading: false });

          // Limpiar estado anónimo
          set({
            anonymousSessionToken: null,
            isAnonymous: false,
          });

          // Recargar carrito como usuario autenticado
          await get().loadCart();

          logger.log("Carrito anónimo convertido a autenticado");
        } catch (error) {
          logger.error("Error convirtiendo carrito anónimo:", error);
          throw error;
        }
      },

      // ✅ MÉTODOS PARA USUARIOS AUTENTICADOS (EXISTENTES)

      addItem: async (newItem) => {
        const { user } = useAuthStore.getState();

        if (!user) {
          // Si no hay usuario autenticado, usar carrito anónimo
          await get().addAnonymousItem(newItem);
          return;
        }

        // 🚀 OPTIMISTIC UPDATE: Actualizar UI inmediatamente
        const optimisticUpdate = () => {
          set((state) => {
            const existingItem = state.items.find((i) => i.id === newItem.id);
            return existingItem
              ? {
                  items: state.items.map((i) =>
                    i.id === newItem.id ? { ...i, quantity: i.quantity + 1 } : i
                  ),
                }
              : { items: [...state.items, { ...newItem, quantity: 1 }] };
          });
        };

        // 1. Actualizar UI inmediatamente (optimistic update)
        optimisticUpdate();

        // 2. Request en background
        try {
          // 3. Petición al backend en background (sin loading flag - ya hay optimistic update)
          // El token JWT se envía automáticamente en el header Authorization por el interceptor
          await cartService.addToCart({
            productId: newItem.id,
            quantity: 1,
          });
          logger.log("✅ Item agregado exitosamente al backend (autenticado)");
        } catch (error) {
          // 4. 🔄 ROLLBACK: Revertir cambios si falla
          logger.error("❌ Error agregando al carrito autenticado:", error);

          // Revertir el optimistic update
          set((state) => {
            const existingItem = state.items.find((i) => i.id === newItem.id);
            if (existingItem && existingItem.quantity > 1) {
              // Si ya existía, decrementar cantidad
              return {
                items: state.items.map((i) =>
                  i.id === newItem.id ? { ...i, quantity: i.quantity - 1 } : i
                ),
              };
            } else {
              // Si era nuevo, remover completamente
              return {
                items: state.items.filter((i) => i.id !== newItem.id),
              };
            }
          });

          // Re-lanzar el error para que el componente pueda manejarlo
          throw error;
        }
      },

      removeItem: (itemId) => {
        set((state) => ({
          items: state.items.filter((item) => item.id !== itemId),
        }));
      },

      updateQuantity: (itemId, quantity) => {
        if (quantity <= 0) {
          get().removeItem(itemId);
          return;
        }

        set((state) => ({
          items: state.items.map((item) =>
            item.id === itemId ? { ...item, quantity } : item
          ),
        }));
      },

      clearCart: async () => {
        try {
          const { user } = useAuthStore.getState();
          const { isAnonymous, anonymousSessionToken } = get();

          logger.log("🧹 Iniciando limpieza de carrito...", {
            isAnonymous,
            hasSessionToken: !!anonymousSessionToken,
            currentItems: get().items.length,
          });

          // 🛡️ ESTABLECER FLAG PARA PREVENIR RECARGAS
          set({ isPostPayment: true });

          // Limpiar backend primero
          if (isAnonymous && anonymousSessionToken) {
            // Limpiar carrito anónimo
            logger.log(
              "🔄 Limpiando carrito anónimo en backend con token:",
              anonymousSessionToken
            );
            const response = await cartService.clearAnonymousCart(
              anonymousSessionToken
            );
            logger.log(
              "✅ Respuesta del backend al limpiar carrito anónimo:",
              response
            );
          } else if (user) {
            // Limpiar carrito autenticado
            // El token JWT se envía automáticamente en el header Authorization por el interceptor
            logger.log(
              "🔄 Limpiando carrito autenticado en backend para user:",
              user.id
            );
            const response = await cartService.clearCart(user.id);
            logger.log(
              "✅ Respuesta del backend al limpiar carrito autenticado:",
              response
            );
          }

          // Limpiar estado local (mantener session token para usuarios anónimos)
          set({
            items: [],
            // ⚠️ NO eliminar anonymousSessionToken - es la sesión del usuario
            lastClearTimestamp: Date.now(), // 🛡️ Marcar cuándo se limpió
            isPostPayment: true, // Mantener el flag
          });

          logger.log(
            "✅ Carrito limpiado completamente - items eliminados del estado local"
          );

          // 🔄 Resetear el flag después de 10 segundos (aumentado de 5 a 10)
          setTimeout(() => {
            set({ isPostPayment: false });
            logger.log("🔄 Flag post-pago reseteado después de 10 segundos");
          }, 10000);
        } catch (error) {
          logger.error("❌ Error limpiando carrito:", error);
          logger.error("❌ Detalles completos del error:", JSON.stringify(error, Object.getOwnPropertyNames(error)));
          // Aún así limpiar el estado local
          set({
            items: [],
            lastClearTimestamp: Date.now(),
            isPostPayment: true,
          });
          // Resetear flag también en caso de error
          setTimeout(() => {
            set({ isPostPayment: false });
          }, 10000);
          // Re-lanzar el error para que el componente pueda manejarlo
          throw error;
        }
      },

      clearCartAfterPayment: () => {
        // Limpiar completamente el estado del carrito después de un pago exitoso
        logger.log("🧹 Limpiando carrito después del pago exitoso...");
        set({
          items: [],
          anonymousSessionToken: null,
          isAnonymous: false,
          isPostPayment: true, // 🛡️ Marcar como post-pago
          lastClearTimestamp: Date.now(), // 🛡️ Marcar cuándo se limpió
        });
        logger.log("✅ Carrito limpiado completamente después del pago");

        // 🔄 Resetear el flag después de 5 segundos
        setTimeout(() => {
          set({ isPostPayment: false });
          logger.log("🔄 Flag post-pago reseteado");
        }, 5000);
      },

      getTotalItems: () => {
        return get().items.reduce((total, item) => total + item.quantity, 0);
      },

      getTotalPrice: () => {
        // Ensure we handle items that may not have a valid price yet
        return get().items.reduce((total, item) => {
          const price =
            typeof item.price === "number" && !isNaN(item.price)
              ? item.price
              : 0;
          return total + price * item.quantity;
        }, 0);
      },

      loadCart: async () => {
        try {
          logger.log("🚀 Cargando carrito... usando loadCart");
          const { user } = useAuthStore.getState();
          const { isAnonymous, isPostPayment, lastClearTimestamp } = get();

          // 🛡️ No cargar carrito si estamos en estado post-pago
          if (isPostPayment) {
            logger.log("⏭️ Omitiendo carga de carrito en estado post-pago");
            return;
          }

          // 🛡️ No cargar carrito si fue limpiado recientemente (últimos 5 segundos)
          if (lastClearTimestamp && Date.now() - lastClearTimestamp < 5000) {
            logger.log(
              "⏭️ Omitiendo carga de carrito - limpiado recientemente"
            );
            return;
          }

          if (isAnonymous) {
            // Cargar carrito anónimo
            await get().loadAnonymousCart();
          } else if (user) {
            // Cargar carrito autenticado
            logger.log("user", user);

            // 1. Petición al backend para obtener los items del carrito
            // El token JWT se envía automáticamente en el header Authorization por el interceptor
            const backendItems = await cartService.getCartItems(user.id);
            const products = await productService.getAllProducts();

            // 2. Transformar los datos al formato del estado local
            const transformedItems = backendItems.map((item: any) => ({
              id: item.productId,
              cartItemId: item.id, // Guardar el ID del item del carrito del backend
              name:
                products.products.find((p) => p.id === item.productId)
                  ?.nombre || "",
              price: parseFloat(item.unitPrice),
              quantity: item.quantity,
              image:
                products.products.find((p) => p.id === item.productId)
                  ?.imagen || "",
            }));

            logger.log("transformedItems", transformedItems);

            // 3. Actualizar estado local con los items recuperados
            set({ items: transformedItems as CartItem[] });
          }
        } catch (error) {
          logger.error("No se pudo obtener el carrito del backend", error);
        }
      },

      removeItemFromBackend: async (cartItemId: number) => {
        try {
          const { isAnonymous, anonymousSessionToken } = get();

          if (isAnonymous && anonymousSessionToken) {
            // Eliminar item anónimo
            await get().removeAnonymousItemFromBackend(cartItemId);
          } else {
            // Eliminar item autenticado
            // El token JWT se envía automáticamente en el header Authorization por el interceptor
            set({ loading: true });
            await cartService.removeCartItem(cartItemId.toString());
            set({ loading: false });
            await get().loadCart();
          }
        } catch (error) {
          logger.error("Error eliminando item del carrito:", error);
          throw error;
        }
      },

      updateQuantityInBackend: async (cartItemId: number, quantity: number) => {
        try {
          const { isAnonymous, anonymousSessionToken } = get();

          if (isAnonymous && anonymousSessionToken) {
            // Actualizar cantidad anónima (ya hace la petición al backend y recarga)
            await get().updateAnonymousQuantityInBackend(cartItemId, quantity);
          } else {
            // Actualizar cantidad autenticada
            // El token JWT se envía automáticamente en el header Authorization por el interceptor
            set({ loading: true });
            await cartService.updateCartItemQuantity(cartItemId.toString(), {
              quantity,
            });
            set({ loading: false });
            await get().loadCart();
          }
        } catch (error) {
          logger.error("Error actualizando cantidad del item:", error);
          throw error;
        }
      },

      // 🚀 MÉTODOS PARA OPTIMISTIC UPDATES
      updateItemQuantityOptimistic: (cartItemId: number, quantity: number) => {
        logger.log("🚀 Optimistic update quantity:", { cartItemId, quantity });
        set((state) => {
          const updatedItems = state.items.map((item) =>
            item.cartItemId === cartItemId ? { ...item, quantity } : item
          );
          logger.log("📦 Items after optimistic update:", updatedItems);
          return { items: updatedItems };
        });
      },

      removeItemOptimistic: (cartItemId: number) => {
        logger.log("🚀 Optimistic remove item:", { cartItemId });
        set((state) => {
          const updatedItems = state.items.filter(
            (item) => item.cartItemId !== cartItemId
          );
          logger.log("📦 Items after optimistic remove:", updatedItems);
          return { items: updatedItems };
        });
      },

      // 🚀 MÉTODOS PARA OPTIMISTIC UPDATES CON FALLBACK
      updateItemQuantityOptimisticWithFallback: (
        identifier: number | string,
        quantity: number
      ) => {
        logger.log("🚀 Optimistic update quantity with fallback:", {
          identifier,
          quantity,
        });
        set((state) => {
          const updatedItems = state.items.map((item) => {
            // Intentar con cartItemId primero, luego con id como fallback
            if (item.cartItemId === identifier || item.id === identifier) {
              return { ...item, quantity };
            }
            return item;
          });
          logger.log(
            "📦 Items after optimistic update with fallback:",
            updatedItems
          );
          return { items: updatedItems };
        });
      },

      removeItemOptimisticWithFallback: (identifier: number | string) => {
        logger.log("🚀 Optimistic remove item with fallback:", { identifier });
        set((state) => {
          const updatedItems = state.items.filter((item) => {
            // Intentar con cartItemId primero, luego con id como fallback
            return item.cartItemId !== identifier && item.id !== identifier;
          });
          logger.log(
            "📦 Items after optimistic remove with fallback:",
            updatedItems
          );
          return { items: updatedItems };
        });
      },

      clearCartOptimistic: () => {
        logger.log("🚀 Optimistic clear cart");
        set({
          items: [],
          // ⚠️ NO eliminar anonymousSessionToken - mantener la sesión
          lastClearTimestamp: Date.now(), // 🛡️ Marcar cuándo se limpió
        });
      },

      // 🚀 MÉTODOS PARA INTEGRACIÓN CON COLA
      updateUIOptimistic: (action: any) => {
        logger.log("🚀 Optimistic update desde CartStore:", action);
        // Esta función será llamada por el CartQueue para actualizar la UI inmediatamente
        // La implementación específica se maneja en el hook useCartQueue
      },

      getQueueStatus: () => {
        // Retornar estado básico de la cola
        return {
          isProcessing: false,
          queueLength: 0,
          pendingActions: [],
        };
      },
    }),
    {
      name: "chilenito-cart",
      partialize: (state) => ({
        items: state.items,
        anonymousSessionToken: state.anonymousSessionToken,
        isAnonymous: state.isAnonymous,
        lastClearTimestamp: state.lastClearTimestamp,
        isPostPayment: state.isPostPayment,
      }),
    }
  )
);
